import {
  computeListMaxHeight,
  computePickerScroll,
  containerContentTop,
  effectiveViewportTop,
  fieldAnchorTop,
  scrollContainerOf,
  visibleViewport,
} from './picker-keyboard-scroll';

/**
 * The motion half of keeping a focused thing clear of the soft keyboard.
 *
 * Split out of `use-picker-keyboard-scroll` so a plain focused input and an open
 * dropdown share one implementation: an input is just a picker whose list is
 * zero pixels tall. The geometry lives in `picker-keyboard-scroll.ts`; this is
 * only the loop that applies it.
 *
 * Ported from VABS, whose two hard-won rules are load-bearing:
 *
 * 1. **A per-frame follow loop, not a scroll animation.** Every frame it
 *    re-measures the target and moves a fraction of the way there. The keyboard
 *    resizes the viewport as it travels, so the target moves too — and because
 *    the loop reads the newest target each frame, a moving target costs nothing.
 *    `behavior: 'smooth'` cannot do this: each new resize restarts its easing
 *    from a standstill, which is what reads as stutter. Don't reintroduce one.
 *
 * 2. **Nothing flips above the anchor.** The geometry always measures the list as
 *    hanging below, so callers with a dropdown must pin it downward.
 */

/**
 * Fraction of the remaining distance covered per frame — the smoothness dial.
 *
 * Lower is gentler and slower: the loop covers `EASE` of what's left each frame,
 * so 95% of the gap closes in `ln(0.05) / ln(1 - EASE)` frames. At 0.18 that's
 * ~15 frames (~250ms) against 0.28's ~9 (~150ms), which read as abrupt on a
 * keyboard that appears in one jump. Below ~0.10 the tail starts to feel like
 * drift rather than motion.
 */
const EASE = 0.18;
/** Never crawl: below this the remaining pixels are covered outright. */
const MIN_STEP = 1;
/** Frames with nothing to do before the loop parks itself. */
const IDLE_FRAMES = 6;
/** Frames the container may fail to move while we still want to scroll, before
 *  concluding it cannot (a clipped ancestor, a container at its end). */
const STUCK_FRAMES = 5;
/** Hard ceiling on one follow run, so no combination of measurements can leave a
 *  rAF loop running. */
const MAX_FRAMES = 120;

export interface KeyboardFollowOptions {
  /** The element that must end up visible — a picker's trigger, or the focused
   *  input itself. Its label is protected too, via `fieldAnchorTop`. */
  anchor: HTMLElement;
  /**
   * Content hanging BELOW the anchor that must also clear the keyboard, read
   * fresh each frame because it mounts and resizes as the user types. Return
   * null for a plain input — there's nothing below it.
   */
  list?: () => HTMLElement | null;
  /**
   * How far out of place the anchor may be before it's worth moving at all.
   * Defaults to `MIN_SCROLL`; a bare field wants the much wider
   * `FIELD_MIN_SCROLL` so it isn't nudged again after the browser's own
   * scroll-into-view has already parked it. See those constants.
   */
  deadZone?: number;
}

/**
 * Starts following. Returns a cleanup that stops the loop, drops any listeners
 * and removes the spacer — call it when the picker closes or the field blurs.
 */
export function startKeyboardFollow({ anchor, list, deadZone }: KeyboardFollowOptions): () => void {
  const root = scrollContainerOf(anchor);
  let spacer: HTMLDivElement | null = null;
  let raf = 0;
  let moving = false;
  let idle = 0;
  let stuck = 0;
  let frames = 0;
  let lastTop = root ? root.scrollTop : 0;

  const makeRoom = (padBottom: number): void => {
    if (!root || padBottom <= 0) return;
    // A field near the end of its container has nothing left to scroll; a spacer
    // creates the room and is removed again on cleanup.
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.setAttribute('aria-hidden', 'true');
      spacer.dataset.keyboardFollowSpacer = '';
      root.appendChild(spacer);
    }
    const current = parseFloat(spacer.style.height || '0');
    spacer.style.height = `${Math.ceil(Math.max(current, padBottom))}px`;
  };

  const frame = (): void => {
    raf = 0;
    frames += 1;
    const rect = anchor.getBoundingClientRect();
    const listEl = list?.() ?? null;
    const listHeight = listEl?.offsetHeight ?? 0;
    const { top: viewportTop, bottom: viewportBottom } = visibleViewport();
    const { scrollBy, padBottom } = computePickerScroll({
      wrapperTop: rect.top,
      wrapperBottom: rect.bottom,
      // Protect the label too, not just the control — otherwise the form scrolls
      // until the user is looking at an unlabelled box.
      anchorTop: fieldAnchorTop(anchor),
      listHeight,
      // Clamp against the scrolling panel, not just the screen: measuring from
      // the screen lets the field slide up under the dialog's title bar. That bar
      // is `position: sticky` INSIDE the scroll container, so the container's own
      // top edge is above it and is not the boundary — `containerContentTop`
      // raises it to the bottom of anything tagged `data-sticky-top`.
      viewportTop: effectiveViewportTop(viewportTop, containerContentTop(root)),
      viewportBottom,
      maxScroll: root ? root.scrollHeight - root.clientHeight - root.scrollTop : 0,
      // Once moving, close the gap completely; the dead zone only governs whether
      // a settled field is disturbed in the first place.
      minScroll: moving ? 0 : deadZone,
    });

    // Cap a dropdown to the room it will have once the scroll completes, so a
    // short screen shrinks the list instead of pushing it under the keyboard.
    if (listEl) {
      const maxHeight = computeListMaxHeight({
        wrapperBottom: rect.bottom,
        scrollBy: root ? scrollBy : 0,
        viewportBottom,
      });
      const next = `${maxHeight}px`;
      if (listEl.style.maxHeight !== next) listEl.style.maxHeight = next;
    }

    if (!root) return;

    if (scrollBy <= 0) {
      moving = false;
      idle += 1;
      // Keep watching briefly: the keyboard may still be on its way, and the
      // viewport listener would otherwise have to restart the loop.
      if (idle < IDLE_FRAMES && frames < MAX_FRAMES) raf = requestAnimationFrame(frame);
      return;
    }

    idle = 0;
    moving = true;
    makeRoom(padBottom);
    const step = Math.min(scrollBy, Math.max(MIN_STEP, scrollBy * EASE));
    root.scrollTop += step;

    // Nothing actually moved — the container is clipped or already at its end.
    // Give it a few frames (the spacer needs one to lay out) then stop rather
    // than spin.
    stuck = root.scrollTop === lastTop ? stuck + 1 : 0;
    lastTop = root.scrollTop;
    if (stuck >= STUCK_FRAMES || frames >= MAX_FRAMES) return;

    raf = requestAnimationFrame(frame);
  };

  const start = (): void => {
    frames = 0;
    idle = 0;
    stuck = 0;
    if (!raf) raf = requestAnimationFrame(frame);
  };

  start();

  // Each viewport change (the keyboard arriving, an accessory bar settling)
  // re-arms the loop; a run already in flight just keeps going, since it re-reads
  // the target every frame anyway.
  const vv = window.visualViewport;
  vv?.addEventListener('resize', start);
  vv?.addEventListener('scroll', start);

  // Never fight the user: their own scroll wins and ends the follow.
  const stop = (): void => {
    cancelAnimationFrame(raf);
    raf = 0;
    frames = MAX_FRAMES;
  };
  root?.addEventListener('touchstart', stop, { passive: true });
  root?.addEventListener('wheel', stop, { passive: true });

  return () => {
    vv?.removeEventListener('resize', start);
    vv?.removeEventListener('scroll', start);
    root?.removeEventListener('touchstart', stop);
    root?.removeEventListener('wheel', stop);
    cancelAnimationFrame(raf);
    spacer?.remove();
  };
}
